import * as React from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import memoizeOne from "memoize-one";

import { EntityView } from "api/generated/model";
import { RadioInput } from "components/form/legacyFields/radioInput";
import {
    SelectInput,
    SelectInputOption,
} from "components/form/legacyFields/selectInput";
import * as T from "org/risks/content/types";
import {
    FormComponentProps,
    mapForm,
    simpleMappers,
    subForm,
} from "utils/form";

class NodeScope<ID extends string, ROOT> extends React.PureComponent<
    NodeScopeProps<ID, ROOT>
> {
    entitiesOptions = memoizeOne(
        (
            entities: Record<number, EntityView>,
            entitiesWithWritePermission?: number[]
        ) => {
            const options: Array<SelectInputOption> = [];
            Object.values(entities).forEach((entity) => {
                if (
                    entitiesWithWritePermission === undefined ||
                    entitiesWithWritePermission.includes(entity.id)
                ) {
                    options.push({
                        value: entity.id.toString(),
                        label: entity.name,
                    });
                }
            });
            options.sort(Cmp.map((option) => option.label, Cmp.string()));
            return options;
        }
    );

    render = () => {
        switch (this.props.type) {
            case "READ_ONLY":
                return this.renderReadOnly(this.props);
            case "CREATE":
                return this.renderCreate(this.props);
            default:
                assertNever(this.props);
        }
    };

    renderReadOnly = (
        props: NodeScopePropsReadOnly & NodeScopePropsCommon
    ): React.ReactNode => {
        const { entities, scope } = props;

        return (
            <>
                <RadioInput
                    type="READ_ONLY"
                    label={t("RiskDirectory_Scope")}
                    options={[
                        { value: "Global", label: this.globalLabel() },
                        { value: "Local", label: this.localLabel() },
                    ]}
                    value={scope}
                    columns={2}
                />
                {props.scope === "Local" ? (
                    <SelectInput
                        type="READ_ONLY"
                        label={t("RiskMapResponse_Entity")}
                        options={this.entitiesOptions(entities)}
                        value={props.entityId.toString()}
                    />
                ) : null}
            </>
        );
    };

    renderCreate = (
        props: NodeScopePropsCreate<ID, ROOT> & NodeScopePropsCommon
    ): React.ReactNode => {
        const { initialValue, form, id, entities, permission } = props;
        const mapper = simpleMappers<
            "entityId",
            number | undefined,
            string,
            ROOT
        >(
            "entityId",
            (a: number | undefined) => (a !== undefined ? a.toString() : ""),
            (b: string) => {
                const parsed = Number(b);
                if (isNaN(parsed)) {
                    return undefined;
                }
                return parsed;
            }
        );
        const localForm = mapForm<NativeNodeScopeValues>()(
            subForm(id, form),
            mapper()
        );
        const initScope = initialScope(permission, initialValue);
        const scope = localForm.getFieldValue("scope") ?? initScope;

        return (
            <>
                <RadioInput
                    id="scope"
                    type="CREATE"
                    label={t("RiskDirectory_Scope")}
                    options={[
                        {
                            value: "Global",
                            label: this.globalLabel(),
                            disabled: globalDisabled(permission),
                        },
                        {
                            value: "Local",
                            label: this.localLabel(),
                            disabled: localDisabled(permission),
                        },
                    ]}
                    initialValue={initScope}
                    form={localForm}
                    columns={2}
                    validators={[{ kind: "ruleset", required: true }]}
                />
                {scope === "Local" ? (
                    <SelectInput
                        id="entityId"
                        type="CREATE"
                        label={t("RiskDirectory_Scope_ChooseLocalEntity")}
                        options={this.entitiesOptions(
                            entities,
                            entityIds(permission)
                        )}
                        initialValue={initialEntityId(
                            permission,
                            initialValue
                        ).toString()}
                        form={localForm}
                        disabled={
                            permission.type === "ChildOfLocalNodePermission"
                        }
                    />
                ) : null}
            </>
        );
    };

    globalLabel = (): string => {
        const { nodeType } = this.props;
        switch (nodeType) {
            case "Process":
                return t("RiskDirectory_Scope_GlobalProcess");
            case "Risk":
                return t("RiskDirectory_Scope_GlobalRisk");
            default:
                assertNever(nodeType);
        }
    };

    localLabel = (): string => {
        const { nodeType } = this.props;
        switch (nodeType) {
            case "Process":
                return t("RiskDirectory_Scope_LocalProcess");
            case "Risk":
                return t("RiskDirectory_Scope_LocalRisk");
            default:
                assertNever(nodeType);
        }
    };
}

function initialScope(
    permission: T.NodeCreationAllowedPermission,
    initialValue: T.NodeScope | NodeScopeValues | undefined
): "Global" | "Local" {
    switch (permission.type) {
        case "GlobalAndLocalPermission":
            return initialValue?.scope ?? "Global";
        case "GlobalOnlyPermission":
            return "Global";
        case "LocalOnlyPermission":
        case "ChildOfLocalNodePermission":
            return "Local";
        default:
            assertNever(permission);
    }
}

function initialEntityId(
    permission: T.NodeCreationAllowedPermission,
    initialValue: T.NodeScope | NodeScopeValues | undefined
): number {
    const wantedEntityId =
        initialValue?.scope === "Local" ? initialValue.entityId : undefined;
    switch (permission.type) {
        case "ChildOfLocalNodePermission":
            return permission.entityId;
        case "GlobalAndLocalPermission":
        case "LocalOnlyPermission":
            if (
                wantedEntityId !== undefined &&
                permission.entityIds.includes(wantedEntityId)
            ) {
                return wantedEntityId;
            } else {
                return permission.entityIds[0];
            }
        case "GlobalOnlyPermission":
            throw new Error("Cannot create a local node");
        default:
            assertNever(permission);
    }
}

function globalDisabled(permission: T.NodeCreationAllowedPermission): boolean {
    switch (permission.type) {
        case "LocalOnlyPermission":
        case "ChildOfLocalNodePermission":
            return true;
        case "GlobalOnlyPermission":
        case "GlobalAndLocalPermission":
            return false;
        default:
            assertNever(permission);
    }
}

function localDisabled(permission: T.NodeCreationAllowedPermission): boolean {
    switch (permission.type) {
        case "ChildOfLocalNodePermission":
        case "GlobalOnlyPermission":
            return true;
        case "GlobalAndLocalPermission":
        case "LocalOnlyPermission":
            return false;
        default:
            assertNever(permission);
    }
}

function entityIds(permission: T.NodeCreationAllowedPermission): number[] {
    switch (permission.type) {
        case "GlobalAndLocalPermission":
        case "LocalOnlyPermission":
            return permission.entityIds;
        case "ChildOfLocalNodePermission":
            return [permission.entityId];
        case "GlobalOnlyPermission":
            throw new Error("Cannot create a local node");
        default:
            assertNever(permission);
    }
}

type NodeScopePropsReadOnly =
    | {
          scope: "Global";
      }
    | {
          scope: "Local";
          entityId: number;
      };

interface NodeScopePropsCreate<
    ID extends string,
    ROOT,
> extends FormComponentProps<ID, NodeScopeValues, ROOT> {
    id: ID;
    initialValue?: T.NodeScope;
    permission: T.NodeCreationAllowedPermission;
}

interface NodeScopePropsCommon {
    entities: Record<number, EntityView>;
    nodeType: "Process" | "Risk";
}

type NodeScopeProps<ID extends string, ROOT> = (
    | ({ type: "READ_ONLY" } & NodeScopePropsReadOnly)
    | ({ type: "CREATE" } & NodeScopePropsCreate<ID, ROOT>)
) &
    NodeScopePropsCommon;

// Discriminated Unions aren't supported by form.
interface NodeScopeValues {
    scope: "Global" | "Local";
    entityId: number | undefined;
}

interface NativeNodeScopeValues {
    scope: "Global" | "Local";
    entityId: string;
}

export type {
    NodeScopeProps,
    NodeScopeValues,
    NodeScopePropsReadOnly,
    NodeScopePropsCreate,
};
export { NodeScope, initialScope, initialEntityId };
